// Generic Kanban Board — HTML5 native drag-drop, no external dependency.
// Usage:
//   <KanbanBoard
//     columns={[{ key, label, color?, accent? }]}
//     cards={[{ id, columnKey, ...anyFields }]}
//     renderCard={(card) => <JSX>}
//     onMove={async (cardId, fromKey, toKey) => { /* persist */ }}
//     onCardClick={(card) => {}}
//     loading={false}
//     emptyText="Kart yok"
//   />
//
// Optimistic update: card UI moves immediately; if onMove throws, it reverts.
import React, { useMemo, useState, useCallback } from 'react';

export default function KanbanBoard({
  columns = [],
  cards = [],
  renderCard,
  onMove,
  onCardClick,
  loading = false,
  emptyText = 'Kart yok',
  columnHeaderExtra,   // (col, count) => JSX  — örn. toplam tutar
  cardKey = 'id',
  cardColumnKey = 'columnKey',
}) {
  const [overCol, setOverCol] = useState(null);
  const [optimistic, setOptimistic] = useState({}); // { [cardId]: newColumnKey }
  const [dragId, setDragId] = useState(null);

  const cardsByCol = useMemo(() => {
    const map = {};
    columns.forEach(c => { map[c.key] = []; });
    cards.forEach(c => {
      const id = c[cardKey];
      const colKey = optimistic[id] || c[cardColumnKey];
      if (!map[colKey]) map[colKey] = [];
      map[colKey].push(c);
    });
    return map;
  }, [columns, cards, optimistic, cardKey, cardColumnKey]);

  const handleDragStart = useCallback((e, card) => {
    const id = card[cardKey];
    setDragId(id);
    e.dataTransfer.setData('text/plain', String(id));
    e.dataTransfer.effectAllowed = 'move';
  }, [cardKey]);

  const handleDragEnd = useCallback(() => {
    setDragId(null);
    setOverCol(null);
  }, []);

  const handleDragOver = useCallback((e, colKey) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (overCol !== colKey) setOverCol(colKey);
  }, [overCol]);

  const handleDragLeave = useCallback((e) => {
    // Only clear if leaving the column entirely (not entering a child)
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setOverCol(null);
  }, []);

  const handleDrop = useCallback(async (e, toCol) => {
    e.preventDefault();
    setOverCol(null);
    const id = e.dataTransfer.getData('text/plain');
    if (!id) return;
    const card = cards.find(c => String(c[cardKey]) === String(id));
    if (!card) return;
    const fromCol = card[cardColumnKey];
    if (fromCol === toCol) return;

    setOptimistic(prev => ({ ...prev, [id]: toCol }));
    try {
      if (onMove) await onMove(id, fromCol, toCol, card);
      setOptimistic(prev => { const n = { ...prev }; delete n[id]; return n; });
    } catch (err) {
      console.error('Kanban onMove error:', err);
      setOptimistic(prev => { const n = { ...prev }; delete n[id]; return n; });
    }
  }, [cards, cardKey, cardColumnKey, onMove]);

  return (
    <div className="kanban-board" style={S.board}>
      {columns.map(col => {
        const list = cardsByCol[col.key] || [];
        const isOver = overCol === col.key;
        const accent = col.accent || col.color || 'var(--text-tertiary)';
        return (
          <div
            key={col.key}
            className={'kanban-col' + (isOver ? ' over' : '')}
            style={{ ...S.col, ...(isOver ? S.colOver : null) }}
            onDragOver={(e) => handleDragOver(e, col.key)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, col.key)}
          >
            <div style={{ ...S.colHeader, borderTopColor: accent }}>
              <div style={S.colHeaderLeft}>
                <span style={{ ...S.colDot, background: accent }} />
                <span style={S.colLabel}>{col.label}</span>
                <span style={S.colCount}>{list.length}</span>
              </div>
              {columnHeaderExtra && (
                <div style={S.colHeaderRight}>{columnHeaderExtra(col, list)}</div>
              )}
            </div>

            <div style={S.colBody}>
              {loading && list.length === 0 && (
                <div style={S.skeletonWrap}>
                  {[1, 2, 3].map(i => <div key={i} style={S.skeleton} />)}
                </div>
              )}
              {!loading && list.length === 0 && (
                <div style={S.emptyCol}>{emptyText}</div>
              )}
              {list.map(card => {
                const id = card[cardKey];
                const isDragging = String(dragId) === String(id);
                return (
                  <div
                    key={id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, card)}
                    onDragEnd={handleDragEnd}
                    onClick={() => onCardClick && onCardClick(card)}
                    style={{
                      ...S.card,
                      ...(isDragging ? S.cardDragging : null),
                      borderLeftColor: accent,
                    }}
                  >
                    {renderCard ? renderCard(card) : (
                      <div style={S.cardDefault}>
                        <div style={S.cardTitle}>{card.title || card.name || id}</div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

const S = {
  board: {
    display: 'flex',
    gap: 12,
    padding: 12,
    overflowX: 'auto',
    alignItems: 'flex-start',
    minHeight: 'calc(100vh - 180px)',
    background: 'var(--bg-primary)',
  },
  col: {
    flex: '0 0 300px',
    width: 300,
    background: 'var(--bg-secondary)',
    borderRadius: 'var(--r-lg)',
    border: '1px solid var(--border-secondary)',
    display: 'flex',
    flexDirection: 'column',
    maxHeight: 'calc(100vh - 200px)',
    boxShadow: 'var(--shadow-xs)',
    transition: 'border-color 0.15s, background 0.15s, box-shadow 0.15s',
  },
  colOver: {
    borderColor: 'var(--accent-primary)',
    background: 'var(--bg-card-hover)',
    boxShadow: 'var(--shadow-glow)',
  },
  colHeader: {
    padding: '10px 12px',
    borderTop: '3px solid',
    borderTopLeftRadius: 'var(--r-lg)',
    borderTopRightRadius: 'var(--r-lg)',
    borderBottom: '1px solid var(--border-secondary)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    background: 'var(--bg-card)',
    flexShrink: 0,
  },
  colHeaderLeft: { display: 'flex', alignItems: 'center', gap: 8 },
  colHeaderRight: { fontSize: 'var(--text-xs)', color: 'var(--text-secondary)' },
  colDot: {
    width: 8, height: 8, borderRadius: '50%',
  },
  colLabel: {
    fontSize: 'var(--text-sm)',
    fontWeight: 700,
    color: 'var(--text-primary)',
    letterSpacing: '-0.01em',
  },
  colCount: {
    fontSize: '0.7rem',
    fontWeight: 700,
    color: 'var(--text-secondary)',
    background: 'var(--bg-tertiary)',
    padding: '1px 7px',
    borderRadius: 'var(--r-full)',
    minWidth: 20,
    textAlign: 'center',
  },
  colBody: {
    padding: 8,
    overflowY: 'auto',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  card: {
    background: 'var(--bg-card)',
    border: '1px solid var(--border-secondary)',
    borderLeft: '3px solid',
    borderRadius: 'var(--r-md)',
    padding: '10px 11px',
    cursor: 'grab',
    boxShadow: 'var(--shadow-xs)',
    transition: 'transform 0.12s, box-shadow 0.12s, opacity 0.12s',
    userSelect: 'none',
  },
  cardDragging: {
    opacity: 0.4,
    transform: 'scale(0.98)',
  },
  cardDefault: {},
  cardTitle: {
    fontSize: 'var(--text-sm)',
    fontWeight: 600,
    color: 'var(--text-primary)',
  },
  emptyCol: {
    padding: '24px 8px',
    textAlign: 'center',
    fontSize: 'var(--text-xs)',
    color: 'var(--text-tertiary)',
    fontStyle: 'italic',
  },
  skeletonWrap: { display: 'flex', flexDirection: 'column', gap: 6 },
  skeleton: {
    height: 56,
    background: 'linear-gradient(90deg, var(--bg-tertiary) 0%, var(--bg-card-hover) 50%, var(--bg-tertiary) 100%)',
    backgroundSize: '200% 100%',
    animation: 'kanban-skel 1.2s ease-in-out infinite',
    borderRadius: 'var(--r-md)',
  },
};

// Inject keyframes once
if (typeof document !== 'undefined' && !document.getElementById('kanban-keyframes')) {
  const s = document.createElement('style');
  s.id = 'kanban-keyframes';
  s.textContent = '@keyframes kanban-skel { 0%{background-position:200% 0} 100%{background-position:-200% 0} }';
  document.head.appendChild(s);
}
